import React, { useState, useEffect, createContext, useContext, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    // Belt-and-suspenders: also fire after next paint frame
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }, [pathname]);
  return null;
}

// Analytics
import { initializeGA4, trackPageView } from "@/utils/analytics";
import CookieConsent from "@/components/CookieConsent";

// Pages
// HomePage stays eagerly loaded — it's the LCP route for most first-visit
// traffic, so code-splitting it would add an extra chunk request to the
// critical path. Every other route is lazy-loaded and ships in its own
// chunk so the initial bundle drops from ~1.8 MB to well under 1 MB.
import HomePage from "@/pages/HomePage";
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const BookingPage = lazy(() => import("@/pages/BookingPage"));
const BookingSuccessPage = lazy(() => import("@/pages/BookingSuccessPage"));
const BookingCancelPage = lazy(() => import("@/pages/BookingCancelPage"));
const PackageSuccessPage = lazy(() => import("@/pages/PackageSuccessPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
// RegisterPage removed — no public registration
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const AdminSetupPage = lazy(() => import("@/pages/AdminSetupPage"));
const PasswordResetPage = lazy(() => import("@/pages/PasswordResetPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ClientProfilePage = lazy(() => import("@/pages/ClientProfilePage"));
const BookingDetailsPage = lazy(() => import("@/pages/BookingDetailsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsConditionsPage = lazy(() => import("@/pages/TermsConditionsPage"));
const ConditionsPage = lazy(() => import("@/pages/ConditionsPage"));
const TorticollisPage = lazy(() => import("@/pages/conditions/TorticollisPage"));
const PlagiocephalyPage = lazy(() => import("@/pages/conditions/PlagiocephalyPage"));
const HypermobilityPage = lazy(() => import("@/pages/conditions/HypermobilityPage"));
const AdaptedTaekwondoPage = lazy(() => import("@/pages/services/AdaptedTaekwondoPage"));
const ArchivedBookingsPage = lazy(() => import("@/pages/ArchivedBookingsPage"));
const CaseManagersPage = lazy(() => import("@/pages/CaseManagersPage"));
const SpecialistServicesPage = lazy(() => import("@/pages/SpecialistServicesPage"));
const AdditionalServicesPage = lazy(() => import("@/pages/AdditionalServicesPage"));
const SecurityPrivacyPage = lazy(() => import("@/pages/SecurityPrivacyPage"));
const InsurancePage = lazy(() => import("@/pages/InsurancePage"));
// SEO location pages — separate chunks so they don't bloat the main bundle
const ChildPhysioSandyPage = lazy(() => import("@/pages/locations/ChildPhysioSandyPage"));
const ChildPhysioBedfordPage = lazy(() => import("@/pages/locations/ChildPhysioBedfordPage"));
const ChildPhysioHitchinPage = lazy(() => import("@/pages/locations/ChildPhysioHitchinPage"));

// Components (chrome — stays eager)
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MobileBookAppointmentBar from "@/components/MobileBookAppointmentBar";
import { PublicPageReadinessProvider, PublicRouteBoundary, usePublicPageReady } from "@/components/PublicPageReadiness";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ── Global axios 401 interceptor ─────────────────────────────────────────
// Any API response that returns 401 clears the stale token and bounces the
// user to /login. Without this, an expired JWT (now capped at 4h) or a
// server-side revocation shows up as a broken UI: components silently fail
// to load and the header still shows the "signed-in" avatar. Idempotent
// (registered once at module scope) and safe for the public HomePage
// because it only reacts to 401, not to the /auth/me pre-check that fires
// on mount.
let _authInterceptorInstalled = false;
function installAuthInterceptor() {
  if (_authInterceptorInstalled) return;
  _authInterceptorInstalled = true;
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || "";
      // Don't hijack the login screen: 401 on /auth/login means "wrong
      // password", we want the form to show the message rather than
      // bouncing the user to /login (which they're already on).
      const isAuthEndpoint = /\/auth\/(login|2fa|refresh|register)/.test(url);
      if (status === 401 && !isAuthEndpoint && localStorage.getItem("token")) {
        localStorage.removeItem("token");
        try {
          // Lazy-import to avoid a hard dependency cycle.
          import("./lib/adminCache").then(({ clearAdminCache }) => clearAdminCache());
        } catch { /* ignore */ }
        // Hard navigate so React state (user, token, admin caches) fully
        // resets and the URL query string carries a hint to the login page.
        if (typeof window !== "undefined" && !/\/login/.test(window.location.pathname)) {
          window.location.assign("/login?reason=expired");
        }
      }
      return Promise.reject(error);
    }
  );
}
installAuthInterceptor();

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [idleWarning, setIdleWarning] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (e) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // ── Idle-session timeout ────────────────────────────────────────────────
  // Automatically log the user out after 30 minutes of no activity.
  // Any of mousemove / mousedown / keydown / scroll / touchstart / click
  // resets the timer. We show a soft warning banner 60 s before the
  // forced logout so the user has a chance to stay signed in.
  //
  // Only runs when a user is actually signed in — otherwise there's
  // nothing to log out of.
  useEffect(() => {
    if (!user) return undefined;

    const IDLE_MS = 30 * 60 * 1000;    // 30 minutes
    const WARNING_MS = 60 * 1000;      // 60 s warning before logout
    let logoutTimer = null;
    let warningTimer = null;

    const armTimers = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      if (warningTimer) clearTimeout(warningTimer);
      setIdleWarning(false);
      warningTimer = setTimeout(() => setIdleWarning(true), IDLE_MS - WARNING_MS);
      logoutTimer = setTimeout(() => {
        // Session expired due to inactivity — clear and bounce to login.
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
        setIdleWarning(false);
        try {
          window.location.assign(`/login?reason=idle`);
        } catch { /* SSR / test safety */ }
      }, IDLE_MS);
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const handler = () => armTimers();
    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    armTimers();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      if (logoutTimer) clearTimeout(logoutTimer);
      if (warningTimer) clearTimeout(warningTimer);
    };
  }, [user]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const loginDirect = (accessToken, userData) => {
    localStorage.setItem("token", accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setIdleWarning(false);
    // Clear admin SWR cache so the next login doesn't see stale data.
    try {
      // Lazy import to avoid loading the module for non-admin users.
      import("./lib/adminCache").then(({ clearAdminCache }) => clearAdminCache());
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, loginDirect, register, logout, loading }}>
      {children}
      {idleWarning && user && (
        <div
          role="alert"
          data-testid="idle-warning-banner"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-md bg-[#2D4A3E] text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-3"
        >
          <span className="text-sm font-medium">
            You'll be signed out in under a minute due to inactivity.
          </span>
          <button
            type="button"
            onClick={() => {
              // Any state change through this button also fires a click
              // event which resets the idle timer via the event listener.
              setIdleWarning(false);
            }}
            className="text-sm font-semibold underline underline-offset-2 hover:opacity-90"
            data-testid="idle-warning-stay"
          >
            Stay signed in
          </button>
        </div>
      )}
    </AuthContext.Provider>
  );
};

// Protected Route for authenticated users
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2D4A3E]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Admin Protected Route - requires admin, super_admin, or therapist role
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2D4A3E]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Allow admin, super_admin, therapist, and receptionist roles
  if (!["admin", "super_admin", "therapist", "receptionist"].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout wrapper — hides the public Navbar/Footer on admin routes so the
// new admin shell (AdminLayout) is the only chrome visible inside /admin.
const Layout = ({ children }) => {
  const { loading } = useAuth();
  return <PublicPageReadinessProvider authLoading={loading}><LayoutFrame>{children}</LayoutFrame></PublicPageReadinessProvider>;
};

const LayoutFrame = ({ children }) => {
  const location = useLocation();
  const ready = usePublicPageReady();
  const isAdminRoute = location.pathname.startsWith("/admin") || location.pathname === "/dashboard";
  return (
    <div className={`min-h-screen flex flex-col ${!isAdminRoute ? "public-page-shell" : ""}`}
      data-testid="page-layout" data-public-page-ready={ready ? "true" : "false"}>
      <div className="noise-overlay" />
      {!isAdminRoute && <Navbar />}
      <main className={`flex-1 ${!isAdminRoute ? "pb-[76px] md:pb-0" : ""}`}>
        {children}
      </main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && <MobileBookAppointmentBar />}
    </div>
  );
};

// Page view tracker component
const PageTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Initialize GA4 on app load
    initializeGA4();
  }, []);
  
  useEffect(() => {
    // Track page views on route change
    trackPageView(location.pathname, document.title);
  }, [location]);
  
  return null;
};

// ── Error Boundary ──────────────────────────────────────────────────────
// Catches any render-time exception thrown below it (lazy chunk load
// failures, ReferenceErrors from a bad merge, undefined-access in a
// page component, etc.) and shows a helpful "Something went wrong" card
// instead of the dreaded white screen. Class component because React
// hooks can't act as error boundaries.
//
// Boundary is *below* AuthProvider so the auth context survives a page
// crash — hitting "Reload page" doesn't kick the user back to /login.
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log to the console so preview + prod devtools capture the stack.
    // We deliberately don't POST to a backend endpoint here — the app
    // may already be in a bad state, and a failed report shouldn't
    // become a second exception.
    console.error("Unhandled render error:", error, info?.componentStack);
  }

  handleReset = () => {
    // Try a clean recovery first: clear the boundary state so React
    // re-renders children. If the same code path throws again the
    // boundary catches it a second time.
    this.setState({ error: null });
  };

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        data-testid="route-error-boundary"
        className="min-h-screen flex items-center justify-center bg-[#F5F2ED] px-6"
      >
        <div className="max-w-md w-full bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 mx-auto mb-5 flex items-center justify-center text-3xl font-semibold">!</div>
          <h1 className="text-2xl font-semibold text-[#111827] tracking-tight mb-2">
            Something went wrong
          </h1>
          <p className="text-[15px] text-[#4B5563] mb-6">
            We hit an unexpected error rendering this page. Your data is
            safe — please reload and try again. If the issue persists,
            contact support.
          </p>
          <details className="text-left text-xs text-[#6B7280] mb-6">
            <summary className="cursor-pointer select-none">Technical details</summary>
            <pre className="mt-2 whitespace-pre-wrap break-all bg-[#FAFAFA] rounded-lg p-3 border border-[#F3F4F6]">
              {String(this.state.error?.message || this.state.error || "Unknown error")}
            </pre>
          </details>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="h-10 px-4 rounded-xl border border-[#E5E7EB] text-[13px] font-medium text-[#111827] hover:bg-[#FAFAFA] transition-colors"
              data-testid="route-error-try-again"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="h-10 px-4 rounded-xl bg-[#2D4A3E] text-white text-[13px] font-medium hover:bg-[#1f3629] transition-colors"
              data-testid="route-error-reload"
            >
              Reload page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <PageTracker />
          <Layout>
            <RouteErrorBoundary>
              <PublicRouteBoundary>
                <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              {/* SEO location pages */}
              <Route path="/child-physio-sandy" element={<ChildPhysioSandyPage />} />
              <Route path="/child-physio-bedford" element={<ChildPhysioBedfordPage />} />
              <Route path="/child-physio-hitchin" element={<ChildPhysioHitchinPage />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/booking/success" element={<BookingSuccessPage />} />
              <Route path="/booking/cancel" element={<BookingCancelPage />} />
              <Route path="/packages/success" element={<PackageSuccessPage />} />
              <Route path="/packages/cancel" element={<PackageSuccessPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<Navigate to="/login" replace />} />
              <Route path="/reset-password" element={<PasswordResetPage />} />
              <Route path="/admin-setup" element={<AdminSetupPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsConditionsPage />} />
              <Route path="/conditions-we-treat" element={<ConditionsPage />} />
              <Route path="/conditions/torticollis-treatment-bedford-sandy-hitchin" element={<TorticollisPage />} />
              <Route path="/conditions/plagiocephaly-treatment-bedford-sandy-hitchin" element={<PlagiocephalyPage />} />
              <Route path="/conditions/hypermobility-treatment-bedford-sandy-hitchin" element={<HypermobilityPage />} />
              <Route path="/services/adapted-taekwondo-therapy-bedford-sandy-hitchin" element={<AdaptedTaekwondoPage />} />
              <Route path="/case-managers" element={<CaseManagersPage />} />
              <Route path="/specialist-services" element={<SpecialistServicesPage />} />
              <Route path="/additional-services" element={<AdditionalServicesPage />} />
              <Route path="/security" element={<SecurityPrivacyPage />} />
              <Route path="/insurance" element={<InsurancePage />} />
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboardPage />
                </AdminRoute>
              } />
              <Route path="/admin/clients/:clientId" element={
                <AdminRoute>
                  <ClientProfilePage />
                </AdminRoute>
              } />
              <Route path="/admin/bookings/:bookingId" element={
                <AdminRoute>
                  <BookingDetailsPage />
                </AdminRoute>
              } />
              <Route path="/admin/archived-bookings" element={
                <AdminRoute>
                  <ArchivedBookingsPage />
                </AdminRoute>
              } />
            </Routes>
              </PublicRouteBoundary>
            </RouteErrorBoundary>
          </Layout>
          <CookieConsent />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
